import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store/StoreProvider'
import { formatNoticeDate, subscriptionExpiryIso } from '@utils/subscriptionNotice'
import { t } from '@store/I18nStore'

const LOCKED_PATHS = ['/dialogs', '/visitors', '/control', '/team', '/statistics']
const OPEN_PATHS = ['/billing', '/myProfile', '/settings']
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/reset',
  '/reset-password',
  '/activate',
  '/auth/verify-email',
  '/auth/accept-invite',
  '/offer',
  '/terms',
  '/policy',
  '/status',
]

const KNOWN_APP_PREFIXES = [
  ...PUBLIC_PATHS,
  '/knowledge-base',
  ...LOCKED_PATHS,
  ...OPEN_PATHS,
]

function isLockedPath(pathname: string): boolean {
  return LOCKED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

function isOpenPath(pathname: string): boolean {
  return OPEN_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

function isPublicPath(pathname: string): boolean {
  if (pathname === '/' || pathname.startsWith('/knowledge-base')) return true
  if (PUBLIC_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) return true
  // Unknown URLs render the public 404 page.
  return !KNOWN_APP_PREFIXES.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

/** Blocks working pages when subscription expired. Profile, settings and billing stay open. */
export const WorkspaceAccessGate: React.FC = observer(() => {
  const { billing, auth, emailVerification } = useStore()
  const location = useLocation()
  const lockedPath = isLockedPath(location.pathname)
  const openPath = isOpenPath(location.pathname)
  const awaitingEmail =
    emailVerification.blocked ||
    auth.currentUser?.emailVerified === false ||
    billing.awaitingEmailVerification
  const accessPending =
    billing.accessCheckPending || (billing.loading && !billing.entitlements && !billing.workspaceRestricted)
  const restricted =
    auth.isAuthenticated &&
    !awaitingEmail &&
    (billing.workspaceRestricted || (!billing.accessCheckPending && !billing.canUseWorkspace && Boolean(billing.entitlements)))

  useEffect(() => {
    if (restricted && lockedPath && billing.expiredPromptOpen) {
      billing.dismissExpiredPrompt()
    }
  }, [restricted, lockedPath, billing, billing.expiredPromptOpen])

  if (!auth.isAuthenticated || awaitingEmail) return null
  if (isPublicPath(location.pathname)) return null

  if (accessPending) {
    if (!lockedPath) return null
    return (
      <div className="lunch-confirm-backdrop" role="status" aria-live="polite" aria-busy="true">
        <div className="lunch-confirm-dialog">
          <h2 id="workspace-access-title" className="lunch-confirm-title">{t('auth.loading.check_access')}</h2>
          <div className="lunch-confirm-body">
            <p>{t('billing.loading.limits')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!restricted) return null
  if (!billing.workspaceRestricted && !billing.entitlements) return null

  const showLockOverlay = lockedPath
  const showExpiredModal = billing.expiredPromptOpen && !showLockOverlay && (openPath || !lockedPath)
  if (!showLockOverlay && !showExpiredModal) return null

  const isOwner = billing.canManageBilling
  const planName = billing.entitlements?.plan?.name?.trim() || ''
  const expiresAt = billing.notice?.expiresAt || subscriptionExpiryIso(billing.entitlements)
  const dateLabel = formatNoticeDate(expiresAt)
  const owner = billing.entitlements?.owner
  const ownerLine = [owner?.displayName, owner?.email].filter(Boolean).join(', ')

  const title = isOwner ? t('billing.restricted.suspended') : t('billing.restricted.unavailable')
  const restrictedBody = dateLabel
    ? t('billing.subscription.ended_on_restricted', { date: dateLabel })
    : t('billing.restricted.description')
  const body = isOwner
    ? planName && dateLabel
      ? t('billing.subscription.plan_ended_restricted', { plan: `«${planName}»`, date: dateLabel })
      : restrictedBody
    : `${restrictedBody} ${t('billing.restricted.contact_admin')}${ownerLine ? ` — ${ownerLine}` : ''}`

  const mailto =
    !isOwner && owner?.email
      ? `mailto:${encodeURIComponent(owner.email)}?subject=${encodeURIComponent(t('billing.restricted.renew_title'))}`
      : null

  return (
    <div
      className="lunch-confirm-backdrop"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="workspace-access-title"
    >
      <div className="lunch-confirm-dialog">
        <h2 id="workspace-access-title" className="lunch-confirm-title">
          {title}
        </h2>
        <div className="lunch-confirm-body">
          <p>{body}</p>
        </div>
        <div className="lunch-confirm-actions">
          {isOwner && (
            <Link
              to="/billing"
              className="primary-btn lunch-confirm-btn"
              onClick={() => billing.dismissExpiredPrompt()}
            >
              {showLockOverlay ? t('billing.restricted.pay_restore') : t('billing.payment.go_to_pay')}
            </Link>
          )}
          {mailto && (
            <a href={mailto} className="ghost-btn lunch-confirm-btn">{t('billing.restricted.contact_admin')}</a>
          )}
          {showExpiredModal && (
            <button
              type="button"
              className="ghost-btn lunch-confirm-btn"
              onClick={() => billing.dismissExpiredPrompt()}
            >{t('app.actions.got_it')}</button>
          )}
          {showLockOverlay && (
            <Link to="/myProfile" className="ghost-btn lunch-confirm-btn">{t('nav.my_profile')}</Link>
          )}
        </div>
      </div>
    </div>
  )
})
